import { useMemo, useCallback } from "react";
import { FlatList } from "react-native";
import { useLocalSearchParams, usePathname, useRouter } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { sizes } from "@src/constants/sizes";
import { useResponsiveGridColumns } from "@src/hooks/useResponsiveGridColumns";
import type { ScenarioRow, ScenarioSiteGroup } from "../utils/scenario-group";
import { ScenarioCard } from "./ScenarioCard";

interface ScenarioGridProps {
  groups: ScenarioSiteGroup[];
  selectedSiteName?: string | null;
}

// Matches the pre-existing 3-column cap for this grid.
const MAX_COLUMNS = 3;

export function ScenarioGrid({ groups, selectedSiteName }: ScenarioGridProps) {
  const { theme } = useUnistyles();
  const router = useRouter();
  const pathname = usePathname();
  const { selectedScenario } = useLocalSearchParams<{ selectedScenario?: string }>();
  const gridSpacing = theme.spacing["6xl"];
  const {
    numColumns,
    isMeasured,
    onLayout: onGridLayout,
  } = useResponsiveGridColumns(sizes.scenarioCardMinWidth, MAX_COLUMNS, gridSpacing);
  const showTitle = !!selectedSiteName;

  const rows: ScenarioRow[] = useMemo(() => groups.flatMap((g) => g.rows), [groups]);

  const renderItem = useCallback(
    (info: { item: ScenarioRow | undefined }) => {
      if (!info.item) {
        return (
          <View
            style={{
              width: `${100 / numColumns}%`,
              paddingHorizontal: gridSpacing / 2,
              paddingVertical: gridSpacing / 2,
            }}
          />
        );
      }
      const row = info.item;
      return (
        <View
          style={{
            width: `${100 / numColumns}%`,
            paddingHorizontal: gridSpacing / 2,
            paddingVertical: gridSpacing / 2,
          }}
        >
          <ScenarioCard
            scenarioName={row.scenarioName}
            cameraDisplayName={row.cameraDisplayName}
            cameraSubtitle={row.cameraSubtitle}
            isSelected={selectedScenario === row.scenarioUuid}
            onPress={() => {
              if (selectedScenario === row.scenarioUuid) {
                router.replace(pathname as never);
                return;
              }
              const params = new URLSearchParams({ selectedScenario: row.scenarioUuid });
              if (row.scenarioName) params.set("scenarioName", row.scenarioName);
              router.replace(`${pathname}?${params.toString()}` as never);
            }}
          />
        </View>
      );
    },
    [gridSpacing, numColumns, pathname, router, selectedScenario],
  );

  return (
    <View flex={1} style={{ padding: theme.spacing["6xl"] }}>
      <View width="100%" gap={showTitle ? "6xl" : undefined} pb="6xl" style={{ alignSelf: "center", zIndex: 10 }}>
        {showTitle && (
          <Text textStyle="lg/bold" color="text">
            {selectedSiteName} Scenarios
          </Text>
        )}
      </View>

      <View flex={1} alignItems="center" onLayout={onGridLayout}>
        {!isMeasured ?
          null
        : rows.length === 0 ?
          <View flex={1} justifyContent="center" alignItems="center" py="13xl">
            <Text textStyle="md/regular" color="textMuted">
              No scenarios available
            </Text>
          </View>
        : <FlatList
            key={numColumns}
            data={rows}
            renderItem={renderItem}
            keyExtractor={(item, index) => item?.key ?? `empty-${index}`}
            numColumns={numColumns}
            showsVerticalScrollIndicator={false}
            windowSize={5}
            initialNumToRender={numColumns * 4}
            removeClippedSubviews
            style={{ width: "100%" }}
            contentContainerStyle={{
              paddingHorizontal: gridSpacing / 2,
              paddingBottom: theme.spacing["8xl"],
            }}
          />
        }
      </View>
    </View>
  );
}
